import type { AxiosInstance, AxiosResponse, AxiosStatic } from 'axios';
import axios from 'axios';
import cacheAsyncFunction from 'utils/cacheAsyncFunction';

import type {
  Branding,
  KioskPlaceGroup,
  Landmark,
  NearestGeoNodePayload,
  NearestGeoNodeResponse,
  Network,
  PathLocation,
  Place,
  PlaceLocation,
  SatellitePlace,
  ShareToEmailPayload,
  XYtoPOIPayload,
  XYtoPOIPayloadResponse,
} from '../context/types';
import { ensureHttps } from '../utils/url';
import type { ChannelData, Published } from './types/channel';
import type { KioskRegistration } from './types/kioskRegistration';
import type {
  Manifest,
  ManifestDataFile,
  ManifestMapFile,
} from './types/manifest';

export class DiscoveryService {
  private manifest?: Manifest;
  private sdkManifest?: Manifest;

  public constructor(
    private discoveryClient: string,
    private axios: AxiosInstance,
    private useCorsBypass: boolean = false,
  ) {}

  private async getManifestFromPublished(published: Published[]) {
    const targetFile = published.sort((a, b) => {
      if (a.dataSpecVersion > b.dataSpecVersion) return -1;
      if (a.dataSpecVersion < b.dataSpecVersion) return 1;
      return 0;
    })[0].manifestUrl;

    return this.getPackageResource<Manifest>(targetFile!);
  }

  async getManifestData(): Promise<AxiosResponse<Manifest>> {
    const route = '/discovery/kiosk';
    const result = await this.axios.get<ChannelData>(route, {
      params: {
        discoveryClient: this.discoveryClient,
        publishVersion: 'latest',
      },
    });

    const channel = result.data.channels?.find((c) =>
      c.published.find(
        (p) => p.clientType === 'kiosk' && p.dataSpecVersion === 5,
      ),
    );

    const manifest = await this.getManifestFromPublished(
      channel?.published ?? [],
    );
    this.manifest = manifest.data;
    return manifest;
  }

  async getSdkManifestData(): Promise<AxiosResponse<Manifest>> {
    const route = '/discovery/sdk';
    const result = await this.axios.get<ChannelData>(route, {
      params: {
        discoveryClient: this.discoveryClient,
        publishVersion: 'latest',
      },
    });

    const channel = result.data.channels?.find((c) =>
      c.published.find(
        (p) => p.clientType === 'sdk' && p.dataSpecVersion === 2,
      ),
    );

    const manifest = await this.getManifestFromPublished(
      channel?.published ?? [],
    );
    this.sdkManifest = manifest.data;
    return manifest;
  }

  getSiteManifestMapFile(siteId: number): ManifestMapFile | undefined {
    return this.manifest?.maps.find((m) => m.name === `${siteId}.path`);
  }

  getSitePathEtag(siteId: number): string | undefined {
    return this.getSiteManifestMapFile(siteId)?.etag;
  }

  getSitePathUrl(siteId: number): string | undefined {
    const urlPath = this.getSiteManifestMapFile(siteId)?.url;
    if (urlPath && urlPath.endsWith('.zst')) {
      return urlPath.slice(0, -4);
    }
    return urlPath;
  }

  async getSitePath(
    siteId: number,
  ): Promise<
    AxiosResponse<{ nodes: PathLocation[]; locations: PathLocation[] }>
  > {
    const sitePath = this.getSitePathUrl(siteId);
    return this.getPackageResource<{
      nodes: PathLocation[];
      locations: PathLocation[];
    }>(sitePath!);
  }
  // NETWORK
  getNetworkFile(): ManifestDataFile | undefined {
    return this.manifest?.languages
      .find((l) => l.language === 'en')
      ?.data.find((d) => d.type === 'network');
  }
  getNetworkEtag(): string | undefined {
    return this.getNetworkFile()?.etag;
  }

  async getNetworkData(): Promise<AxiosResponse<Network>> {
    const url = this.getNetworkFile()?.url;
    return this.getPackageResource(ensureHttps(url)!);
  }
  // PLACES
  getPlacesFile(): ManifestDataFile | undefined {
    return this.manifest?.languages
      .find((l) => l.language === 'en')
      ?.data.find((d) => d.type === 'places');
  }
  getPlacesEtag(): string | undefined {
    return this.getPlacesFile()?.etag;
  }

  async getPlaces(): Promise<AxiosResponse<Place[]>> {
    const url = this.getPlacesFile()?.url;
    return this.getPackageResource(ensureHttps(url)!);
  }

  // SDK Places
  getSdkPlacesFile(): ManifestDataFile | undefined {
    return this.sdkManifest?.languages
      .find((l) => l.language === 'en')
      ?.data.find((d) => d.type === 'places');
  }
  getSdkPlacesEtag(): string | undefined {
    return this.getSdkPlacesFile()?.etag;
  }
  async getSdkPlaces(): Promise<AxiosResponse<SatellitePlace[]>> {
    let url = this.getSdkPlacesFile()?.url;
    if (url && url.endsWith('.zst')) {
      url = url.slice(0, -4);
    }

    return this.getPackageResource(ensureHttps(url!));
  }

  // LANDMARKS
  getLandmarksFile(): ManifestDataFile | undefined {
    return this.manifest?.languages
      .find((l) => l.language === 'en')
      ?.data.find((d) => d.type === 'landmarks');
  }
  getLandmarksEtag(): string | undefined {
    return this.getLandmarksFile()?.etag;
  }

  async getLandmarks(): Promise<AxiosResponse<Landmark[]>> {
    const url = this.getLandmarksFile()?.url;
    return this.getPackageResource(url!);
  }

  // PLACE GROUPS
  getPlaceGroupsFile(): ManifestDataFile | undefined {
    return this.manifest?.languages
      .find((l) => l.language === 'en')
      ?.data.find((d) => d.type === 'placegroups');
  }

  getPlaceGroupsEtag(): string | undefined {
    return this.getPlaceGroupsFile()?.etag;
  }

  async getPlaceGroups(): Promise<AxiosResponse<KioskPlaceGroup[]>> {
    const url = this.getPlaceGroupsFile()?.url;
    return this.getPackageResource(url!);
  }

  /* BRANDING */
  getBrandingFile(): ManifestDataFile | undefined {
    return this.manifest?.languages
      .find((l) => l.language === 'en')
      ?.data.find((d) => d.type === 'branding');
  }
  getBrandingEtag(): string | undefined {
    return this.getBrandingFile()?.etag;
  }
  async getBranding(): Promise<AxiosResponse<Branding[]>> {
    const url = this.getBrandingFile()?.url;
    return this.getPackageResource(url!);
  }

  private async getPackageResource<T>(url: string): Promise<AxiosResponse<T>> {
    if (this.useCorsBypass) {
      return this.axios.get<T>(`corsbypass?targetFile=${url}`);
    }

    return axios.get<T>(url);
  }

  private async _getKioskRegistration(
    kioskId: string,
  ): Promise<AxiosResponse<KioskRegistration>> {
    const route = `/kiosk/registration/${kioskId}`;
    const result = await this.axios.get<KioskRegistration>(route, {
      params: { discoveryClient: this.discoveryClient },
    });

    return result;
  }

  getNavigationVoiceFile(): ManifestMapFile | undefined {
    return this.manifest?.maps.find(
      (m) => m.name === 'navigation_voice_v2.json',
    );
  }

  getNavigationVoiceFileUrl(): string | undefined {
    const urlPath = this.getNavigationVoiceFile()?.url;
    if (urlPath && urlPath.endsWith('.zst')) {
      return urlPath.slice(0, -4);
    }
    return urlPath;
  }

  async getNavigationVoice(
    siteId: number,
  ): Promise<
    AxiosResponse<{ nodes: PathLocation[]; locations: PathLocation[] }>
  > {
    const dataFile = this.getNavigationVoiceFileUrl();
    return this.getPackageResource<{
      nodes: PathLocation[];
      locations: PathLocation[];
    }>(dataFile!);
  }

  private async _getNearestGeoNode(
    place: PlaceLocation,
  ): Promise<AxiosResponse<NearestGeoNodeResponse>> {
    const route = `/route/nearest-geonode`;
    const sitePathFileUrl = this.getSitePathUrl(place.site) || '';
    const payload: NearestGeoNodePayload = {
      network: this.discoveryClient,
      site: place.site,
      building: place.building,
      floor: place.floor,
      dstKey: place.id,
      fileUrl: sitePathFileUrl,
    };

    const cachedNearestGeoNode = sessionStorage.getItem(
      `nearestGeonode:${JSON.stringify(payload)}`,
    );

    if (cachedNearestGeoNode) {
      return Promise.resolve(JSON.parse(cachedNearestGeoNode));
    }

    const result = await this.axios.post<NearestGeoNodeResponse>(
      route,
      payload,
      {
        params: { discoveryClient: this.discoveryClient },
      },
    );

    sessionStorage.setItem(
      `nearestGeonode:${JSON.stringify(payload)}`,
      JSON.stringify(result),
    );

    return result;
  }

  private async _getXYtoPOI(
    srcBuilding: number,
    srcFloor: number,
    srcX: number,
    srcY: number,
    destination: PlaceLocation,
  ): Promise<AxiosResponse<XYtoPOIPayloadResponse>> {
    const route = `/route/xy-to-poi`;
    const navigationVoiceFile = this.getNavigationVoiceFileUrl() || '';
    const sitePathFile = this.getSitePathUrl(destination.site) || '';
    const payload: XYtoPOIPayload = {
      network: this.discoveryClient,
      site: destination.site,
      srcBuilding: srcBuilding,
      srcFloor: srcFloor,
      srcX: srcX,
      srcY: srcY,
      dstBuilding: destination.building,
      dstFloor: destination.floor,
      dstKey: destination.id,
      fileUrl: sitePathFile,
      navInstructionsFileUrl: navigationVoiceFile,
      navInstructionsLanguage: 'en',
    };
    const result = await this.axios.post<XYtoPOIPayloadResponse>(
      route,
      payload,
      {
        params: { discoveryClient: this.discoveryClient },
      },
    );
    return result;
  }

  async shareToEmail(
    recipients: string[],
    destinationName: string,
    queryParams: { [key: string]: any },
  ): Promise<AxiosResponse<ShareToEmailPayload>> {
    const route = `/email/kioskRouting`;

    const payload: ShareToEmailPayload = {
      recipients: recipients,
      destinationName,
    };

    const result = await this.axios.post(route, payload, {
      params: {
        discoveryClient: this.discoveryClient,
        printMode: true,
        ...queryParams,
      },
    });
    return result;
  }

  public getNearestGeoNode: (
    place: PlaceLocation,
  ) => Promise<AxiosResponse<NearestGeoNodeResponse>> = cacheAsyncFunction(
    this._getNearestGeoNode.bind(this),
    'getNearestGeoNode',
  );

  public getXYtoPOI: (
    srcBuilding: number,
    srcFloor: number,
    srcX: number,
    srcY: number,
    destination: PlaceLocation,
  ) => Promise<AxiosResponse<XYtoPOIPayloadResponse>> = cacheAsyncFunction(
    this._getXYtoPOI.bind(this),
    'getXYtoPOI',
  );

  public getKioskRegistration: (
    kioskId: string,
  ) => Promise<AxiosResponse<KioskRegistration>> = cacheAsyncFunction(
    this._getKioskRegistration.bind(this),
    'getKioskRegistration',
  );
}

let _discoveryServiceInstance: DiscoveryService | undefined;

export function getDiscoveryService(): DiscoveryService | undefined {
  return _discoveryServiceInstance!;
}

export default function DiscoveryServiceFactory(
  baseURL: string,
  discoveryClient: string,
  axios: AxiosStatic,
  useCorsBypass: boolean = false,
): DiscoveryService {
  const instance = axios.create({ baseURL });
  const service = new DiscoveryService(
    discoveryClient,
    instance,
    useCorsBypass,
  );
  _discoveryServiceInstance = service;
  return service;
}
